import { Breadcrumbs, Link, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { breadcrumbMap } from 'src/view/components/navigation/breadcrumbConfig';
import { useAppState } from 'src/state-management/state-provider';
import { startTransition, useMemo } from 'react';

const DynamicBreadcrumbs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useAppState();
  const { selectedProject, selectedSite } = state;

  const breadcrumbs = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const newBreadcrumbs: { text: string; path?: string }[] = [];
    let currentPath = '';
    let projectId: string | null = null;

    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      currentPath += `/${segment}`;

      if (segment === 'project') {
        if (selectedProject?.projectDetails) {
          projectId = selectedProject.projectDetails.project_id;
          newBreadcrumbs.push({
            text: selectedProject.projectDetails.project_name,
            path: `/project/${projectId}/overview`,
          });
        }
        // Skip the project ID segment
        i++;
        if (i < pathSegments.length) {
          projectId = pathSegments[i];
        }
      } else if (segment === 'site') {
        if (selectedSite) {
          newBreadcrumbs.push({ text: selectedSite.site_name });
        }
        // Skip the site ID segment
        i++;
      } else if (segment === 'carbon') {
        // Map carbon to carbon-dashboard
        if (projectId) {
          newBreadcrumbs.push({
            text: breadcrumbMap['carbon-dashboard'] || 'Carbon Dashboard',
            path: `/project/${projectId}/carbon-dashboard`,
          });
        }
      } else if (segment === 'bio-diversity') {
        // Map bio-diversity to bio-diversity-dashboard
        if (projectId) {
          newBreadcrumbs.push({
            text: breadcrumbMap['bio-diversity-dashboard'] || 'Biodiversity Dashboard',
            path: `/project/${projectId}/bio-diversity-dashboard`,
          });
        }
      } else if (segment === 'environment' || segment === 'climate') {
        // Map environment/climate to climate-dashboard
        if (projectId) {
          newBreadcrumbs.push({
            text: breadcrumbMap['climate-dashboard'] || breadcrumbMap['environment-dashboard'] || 'Climate Dashboard',
            path: `/project/${projectId}/climate-dashboard`,
          });
        }
      } else if (breadcrumbMap[segment]) {
        newBreadcrumbs.push({ text: breadcrumbMap[segment], path: currentPath });
      }
    }

    return newBreadcrumbs;
  }, [location.pathname, selectedProject, selectedSite]);

  const handleBreadcrumbClick = (path?: string) => {
    if (path) {
      startTransition(() => {
        navigate(path);
      });
    }
  };

  return (
    <Breadcrumbs
      separator={<NavigateNextIcon sx={{ color: '#666', fontSize: 18 }} />}
      aria-label="breadcrumb"
      sx={{ 
        paddingBottom: 2,
        '& .MuiBreadcrumbs-separator': {
          marginRight: '4px',
          marginLeft: '4px'
        }
      }}
    >
      <Link
        underline="hover"
        color="inherit"
        onClick={() => handleBreadcrumbClick('/')}
        sx={{ 
          cursor: 'pointer', 
          color: '#999', 
          fontSize: '14px', 
          fontWeight: 500,
          '&:hover': { color: '#FFF' }
        }}
      >
        Projects
      </Link>

      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1;

        return isLast ? (
          <Typography
            key={crumb.text + index}
            sx={{ 
              color: '#FFF', 
              fontSize: '14px', 
              fontWeight: 600, 
              letterSpacing: '0.5px' 
            }}
          >
            {crumb.text}
          </Typography>
        ) : (
          <Link
            underline="hover"
            color="inherit"
            key={crumb.text + index}
            onClick={() => handleBreadcrumbClick(crumb.path)}
            sx={{ 
              cursor: 'pointer', 
              color: '#999', 
              fontSize: '14px', 
              fontWeight: 500,
              letterSpacing: '0.5px',
              '&:hover': { color: '#FFF' }
            }}
          >
            {crumb.text}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
};

export default DynamicBreadcrumbs;
